// src/utils/chatMetrics.ts
// Lightweight in-memory metrics store for ChatHub diagnostics
// This store is used by the Live Telemetry section on the ChatHub Diagnostic page

import { useState, useEffect, useCallback } from 'react';

// ─── Types ───────────────────────────────────────────────────────────────────

export type MetricsScope = 'messages' | 'reactions' | 'presence' | 'typing' | 'network';

export interface ChannelMetric {
  key: string;
  joined: boolean;
  joinedAt?: number;
  leftAt?: number;
}

export interface EventMetric {
  scope: MetricsScope;
  counts: Record<string, number>;
  lastAt?: number;
}

export interface NetworkMetric {
  online: boolean;
  lastChangeAt?: number;
}

export interface ChatMetricsSnapshot {
  channels: Record<string, ChannelMetric>;
  events: Record<MetricsScope, EventMetric>;
  network: NetworkMetric;
}

// ─── In-Memory Store ─────────────────────────────────────────────────────────

const channels: Record<string, ChannelMetric> = {};

const events: Record<MetricsScope, EventMetric> = {
  messages: { scope: 'messages', counts: {} },
  reactions: { scope: 'reactions', counts: {} },
  presence: { scope: 'presence', counts: {} },
  typing: { scope: 'typing', counts: {} },
  network: { scope: 'network', counts: {} },
};

const network: NetworkMetric = {
  online: typeof navigator !== 'undefined' ? navigator.onLine : true,
};

// ─── Channel Functions ───────────────────────────────────────────────────────

/**
 * Mark a channel as joined.
 * @param key - Unique identifier for the channel (e.g., "chat_messages:{roomId}")
 */
export function markChannelJoined(key: string): void {
  channels[key] = {
    key,
    joined: true,
    joinedAt: Date.now(),
    leftAt: channels[key]?.leftAt,
  };
}

/**
 * Mark a channel as left.
 * @param key - Unique identifier for the channel
 */
export function markChannelLeft(key: string): void {
  if (channels[key]) {
    channels[key] = {
      ...channels[key],
      joined: false,
      leftAt: Date.now(),
    };
  } else {
    channels[key] = {
      key,
      joined: false,
      leftAt: Date.now(),
    };
  }
}

// ─── Event Functions ─────────────────────────────────────────────────────────

/**
 * Increment an event counter and update lastAt timestamp.
 * @param scope - The scope category (messages, reactions, etc.)
 * @param eventName - The event name to increment (e.g., 'INSERT', 'DELETE')
 */
export function incrementEvent(scope: MetricsScope, eventName: string): void {
  const scopeEvents = events[scope];
  scopeEvents.counts[eventName] = (scopeEvents.counts[eventName] || 0) + 1;
  scopeEvents.lastAt = Date.now();
}

// ─── Network Functions ───────────────────────────────────────────────────────

/**
 * Set the network online status.
 * @param isOnline - Whether the network is online
 */
export function setNetworkOnline(isOnline: boolean): void {
  network.online = isOnline;
  network.lastChangeAt = Date.now();
}

// ─── Reset Function ──────────────────────────────────────────────────────────

/**
 * Reset all event counters and lastAt timestamps.
 * Channels are NOT reset (they reflect current subscription state).
 * Network status is NOT reset (reflects actual browser state).
 */
export function resetMetrics(): void {
  // Reset event counters
  for (const scope of Object.keys(events) as MetricsScope[]) {
    events[scope].counts = {};
    events[scope].lastAt = undefined;
  }
}

// ─── Snapshot Function ───────────────────────────────────────────────────────

/**
 * Get a snapshot of all current metrics.
 * Returns a copy to prevent external mutation.
 */
export function getSnapshot(): ChatMetricsSnapshot {
  return {
    channels: { ...channels },
    events: {
      messages: { ...events.messages, counts: { ...events.messages.counts } },
      reactions: { ...events.reactions, counts: { ...events.reactions.counts } },
      presence: { ...events.presence, counts: { ...events.presence.counts } },
      typing: { ...events.typing, counts: { ...events.typing.counts } },
      network: { ...events.network, counts: { ...events.network.counts } },
    },
    network: { ...network },
  };
}

// ─── React Hook ──────────────────────────────────────────────────────────────

/**
 * React hook that polls the metrics snapshot at a given interval.
 * @param pollMs - Polling interval in milliseconds (default: 500)
 * @returns Current metrics snapshot
 */
export function useChatMetricsSnapshot(pollMs: number = 500): ChatMetricsSnapshot {
  const [snapshot, setSnapshot] = useState<ChatMetricsSnapshot>(getSnapshot);

  const updateSnapshot = useCallback(() => {
    setSnapshot(getSnapshot());
  }, []);

  useEffect(() => {
    // Initial update
    updateSnapshot();

    // Poll at interval
    const intervalId = setInterval(updateSnapshot, pollMs);

    return () => {
      clearInterval(intervalId);
    };
  }, [pollMs, updateSnapshot]);

  return snapshot;
}
